import React, { useEffect, useLayoutEffect, useRef } from "react";
import styled from "styled-components";
import { NavLink, useLocation } from "react-router-dom";
import { Carousel, StagingBadge, Tab } from "@cinesys/ui/components";
import { useInterfaceAccess } from "../../hooks/useInterfaceAccess";
import {
  CONTROL_ROOM_TAB_KEY,
  SPOTTER_INTERFACE_ID,
  SPOTTER_TOP_TABS,
  SPOTTER_WORDMARK,
} from "./config";
import { SPOTTER_STRINGS } from "./strings";
import SpotterMark from "./SpotterMark";
import CinesysLogo, { CINESYS_URL } from "../../components/CinesysLogo";
import SpotterHeaderChanges from "./SpotterHeaderChanges";
import { SpotterChangesPanelProps } from "./SpotterChangesPanel";
import { useControlRoomNeedsAttention } from "./controlRoom/service";

/**
 * `spotter` shared header — the dark sticky bar from the design mock
 * (`docs/cinesys-nfl/portal-mapping-mock_v2.html`'s `<header>` — round 15: this working doc is now
 * gitignored per Addison's PR #210 review, so it's local-only, not present in a fresh clone/CI),
 * shared by `FieldsScreen`, `FrScreen`, and `RenameScreen`. Adapted to real data: the mock
 * hardcodes "Cincinnati Bengals" in the
 * breadcrumb; here it's the org's actual name (`useInterfaceAccess`'s "granted" `customerName`) —
 * NOT the customer's iconik logo (spec: no customer iconik logo anywhere in this app anymore).
 *
 * The mock's `<nav>` (top-level Field Mapping / Glossary & AI Context tabs) is kept as real
 * top-level tabs — REAL route navigation here (`@cinesys/ui`'s `Tab`, as a `NavLink`, inside the
 * `TabsCarousel` below), since these are two pages of the SAME interface, not a cross-plugin nav
 * (that's the thing this app no longer does — see `CustomerLayout`'s doc comment). The mock's
 * per-page "top-level select"
 * dropdowns (view / glossary-field picker) are NOT part of this shared header — each screen renders
 * its own `SpotterViewPicker` bar for that, mirroring the mock's own `.vpick` select exactly —
 * see `FieldsScreen`'s doc comment.
 *
 * Round 9 (item #7): the breadcrumb's old "— iconik domain" suffix text is gone entirely — it read
 * as an internal/technical label ("iconik domain"), not something a customer needed to see. The
 * dot before the org name is literally the mock's own "·" separator punctuation (confirmed against
 * the mock markup), not a status indicator — so it only ever renders paired with what follows it
 * (loading skeleton or the real name), never on its own.
 *
 * Brand mark: `SpotterMark` (a reticle) sits in front of the wordmark, which is now solid white
 * rather than the mock's two-tone "Spot" + amber "ter" — that split only existed to inject accent
 * color into type with no icon to carry it (the FedEx-arrow trick); with the reticle now doing
 * that job, coloring "ter" too was just redundant, not meaningful (see brand-exploration thread).
 * `CinesysLogo` is a separate "Powered by" mark at the far right of the top row — Cinesys is the
 * maker, not the product, so it stays secondary to the Spotter lockup rather than joining it. The
 * whole badge links out to `CINESYS_URL`.
 *
 * Tab identity: this mounts on both `spotter/*` screens, so it's the right (and only) place to set
 * the browser tab's title to Spotter's — NOT `public/index.html`, which is the base document for
 * this whole customer-frontend SPA and also serves AB Vault's Candidate Assignments screen (see
 * `AppRouter.tsx`/`CustomerLayout.tsx`); a global title there would brand-leak into an org
 * provisioned for that interface instead. The effect below restores the previous title on unmount
 * for the same reason.
 *
 * The favicon is deliberately NOT part of that: every interface now shares the one CineSys mark
 * from `public/index.html`. The tab says which product you are in; the icon says whose platform it
 * is. Per-interface favicons (`spotter-favicon.ico`, `ab-vault-favicon.ico`) are gone.
 *
 * Round 14: takes an optional `changes` prop (`SpotterChangesPanelProps`) — when a page passes it,
 * `SpotterHeaderChanges` renders a right-aligned "N unsaved changes" trigger in the tab row, a
 * shortcut to the page's own `SpotterChangesPanel` sidebar that stays reachable at every scroll
 * position/viewport width even when that sidebar isn't on screen (see that component's doc
 * comment for the full mobile-vs-desktop split). `Top` no longer wraps onto multiple lines at
 * narrow widths either — it used to (`flex-wrap: wrap`), which read as the header "going to a
 * column layout" on mobile; now it stays one row and the org breadcrumb truncates with an
 * ellipsis instead of wrapping the row taller.
 */
/** \`--orange*\` overrides re-skin \`SpotterHeaderChanges\`'s shared \`ChangesTrigger\` "pending" tone to
 *  Spotter's own amber accent — no override needed for its \`success\`/\`error\` tones, since the
 *  shared \`--green*\`/\`--red*\` tokens it reads already ARE Spotter's own nflOkSoft/nflDangerSoft
 *  values (see \`ColorProvider\`'s comment on those tokens), not just a similar approximation. */
/** Matches the "mobile" cutoff `FieldsScreen`/`FrScreen`/`RenameScreen` already use for their own
 *  layout media queries. `GamebookScreen`'s `ColheadSticky` also reads this exact value
 *  (hardcoded there, not imported — mirrors how those screens each hardcode their own `700px`
 *  rather than sharing one cross-file constant) to stop assuming this header is always pinned
 *  above it. */
const MOBILE_MAX_WIDTH = "700px";

/** Sticky only above the mobile breakpoint (Addison) — on a phone-width screen a permanently-
 *  docked header eats too much of the already-short viewport, so it reverts to normal flow
 *  (scrolls away with the page) instead. */
const Header = styled.header`
  background: var(--spotterSlate);
  color: #e8ecef;
  padding: 13px 22px 0;
  position: sticky;
  top: 0;
  z-index: 30;

  --orangeChipBackground: var(--spotterAccentSoft);
  --orange400: var(--spotterAccent);
  --orangeBorder: var(--spotterAccentLine);

  @media (max-width: ${MOBILE_MAX_WIDTH}) {
    position: static;
  }
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: nowrap;
`;

const Lockup = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  flex-shrink: 0;
`;

const Brand = styled.div`
  font-weight: 800;
  font-size: 22px;
  letter-spacing: -0.02em;
  color: #fff;
`;

/** \`min-width: 0\` + \`overflow: hidden\`/ellipsis so a long org name truncates instead of forcing
 *  \`Top\` to wrap onto a second line (or overflow) now that \`Top\` stays a single row at every
 *  width — see \`Top\`'s doc comment. */
const Crumb = styled.div`
  font-size: 15px;
  color: var(--spotterHeaderMuted);
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  span {
    color: var(--spotterHeaderCrumbStrong);
    font-weight: 600;
  }
`;

/** Placeholder-width skeleton shown in place of the org name until it has actually loaded — round
 *  9: previously the crumb rendered a loading-placeholder STRING ("your organization") the instant
 *  the page mounted, which read as a real (wrong) org name for a beat rather than a visibly-loading
 *  state. Now nothing renders in the name's place until `useInterfaceAccess` resolves to
 *  "granted", and the name appears together with its leading separator dot in one paint — never a
 *  bare dot with no name after it, and never a fake name.
 *
 * Round 18 (Addison): the SAME treatment now covers everything else in this header that's
 * identifying — `MarkSkeleton`/`BrandSkeleton` below stand in for the reticle + "Spotter"
 * wordmark, and `TabsSkeleton` for the nav tab labels — so an unauthenticated or not-yet-entitled
 * visitor can't tell what this tool is from the header alone. All of them key off the same
 * `access.status === "granted"` boolean, so they flip together in one paint too. */
const CrumbSkeleton = styled.span`
  display: inline-block;
  width: 90px;
  height: 0.85em;
  border-radius: 3px;
  background: var(--spotterHeaderBarTrack);
  vertical-align: -0.1em;
`;

const MarkSkeleton = styled.span`
  display: inline-block;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--spotterHeaderBarTrack);
`;

const BrandSkeleton = styled.span`
  display: inline-block;
  width: 64px;
  height: 20px;
  border-radius: 4px;
  background: var(--spotterHeaderBarTrack);
`;

const TabPillSkeleton = styled.span`
  display: inline-block;
  height: 30px;
  border-radius: 6px;
  background: var(--spotterHeaderBarTrack);
  flex-shrink: 0;
`;

const TabsSkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

/** Same tab COUNT as `SPOTTER_TOP_TABS` (so the skeleton reads as "a tab row", not a random
 *  shape), but fixed widths, not derived from the real labels — deliberately uncorrelated with
 *  actual label length so it gives away nothing about what the tabs say. */
const SKELETON_TAB_WIDTHS = [70, 130, 90, 100, 70];

const SpotterTabsSkeleton = () => (
  <TabsSkeletonRow aria-hidden="true">
    {SPOTTER_TOP_TABS.map((tab, i) => (
      <TabPillSkeleton
        key={tab.key}
        style={{
          width: SKELETON_TAB_WIDTHS[i % SKELETON_TAB_WIDTHS.length],
        }}
      />
    ))}
  </TabsSkeletonRow>
);

const Spacer = styled.div`
  flex: 1;
`;

/** "Powered by Cinesys" — the maker's mark, kept secondary to the Spotter lockup (see doc
 *  comment above). Sized up twice now from the brand-exploration mock's badge (that one was
 *  tuned to sit in a small comparison card, not a real header) so it reads clearly at the far
 *  right here. Links out to Cinesys's own site — it's a real attribution, not a decoration.
 *  `flex-shrink: 0` means it never yields space to its siblings — fine on desktop, but below
 *  the mobile breakpoint `Top` doesn't have room for the lockup, org breadcrumb, AND this
 *  attribution badge in one row (it was pushing past the viewport edge). Dropped entirely on
 *  mobile so the breadcrumb gets that space instead — it's attribution, not something a mobile
 *  visitor needs to tap. */
const PoweredBy = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  text-decoration: none;
  opacity: 0.85;
  transition: opacity 0.15s ease;

  &:hover,
  &:focus-visible {
    opacity: 1;
  }

  @media (max-width: ${MOBILE_MAX_WIDTH}) {
    display: none;
  }
`;

const PoweredByLabel = styled.span`
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--spotterHeaderMuted);
  white-space: nowrap;
`;

/**
 * The tab row is the carousel variant at every viewport width now (Addison: an explicit reversal
 * of the earlier tablet/desktop `Tabs` 2-row-wrap + phone-only-carousel split — see below). It
 * reads `--gray300`/`--blue300` (inactive/active tab color) from the ambient `ColorProvider`
 * palette, overridden LOCALLY on `TabsRow` (their shared ancestor), same pattern
 * `CandidateAssignmentsScreen`'s `StyledSelect` already uses for `@cinesys/ui`'s `Select`.
 */
/** Individual tab button — reuses `Tabs`'s own exported `Tab` (identical active-state/hover
 *  styling, including react-router's automatic `.active` class via `NavLink`, see `Tab`'s own doc
 *  comment) rather than forking a second copy of that CSS. `flex-shrink: 0` so a tab's label
 *  never gets squeezed inside the carousel's `overflow-x: auto` row (same reasoning
 *  `TokenChip.tsx`'s own `TokenButton` uses `flex-shrink: 0` for, on its own carousel).
 *  `scroll-margin-inline` only affects where `scrollIntoView` decides a tab is "fully in view" —
 *  unlike the resting-state padding round 4 removed, it doesn't affect layout, so the flush-edge
 *  resting state stays flush. Without it, a tab scrolled into view lands exactly flush against
 *  the container edge, under `Carousel`'s arrow/fade overlay (45px `FadeRight` plus the arrow
 *  button on top of it); 60px matches the margin Cantemo's own `Tabs.tsx` `scrollTabIntoView`
 *  uses for the same reason. */
const TabButton = styled(Tab)`
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 600;
  padding: 8px 14px;
  border-bottom-width: 2.5px;
  white-space: nowrap;
  scroll-margin-inline: 60px;
`;

/** The Control Room tab's "something needs you" indicator (Addison: "show a little needs
 *  attention icon on the control room tab when you have stuff that needs you? Even when you're on
 *  a different tab?") — a plain inline dot next to the label rather than an absolutely-positioned
 *  corner badge, so it needs no extra positioning context on `TabButton`. Hidden while Control
 *  Room is already the active tab (`SpotterTabs` below) — no point flagging "something here needs
 *  you" on the page you're already looking at. */
const NeedsAttentionDot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-left: 6px;
  border-radius: 50%;
  background: var(--spotterDanger);
  vertical-align: middle;
`;

/** The one tab bar, at every width — engages `Carousel`'s horizontal-scroll/fade/arrow behavior
 *  (arrows overlay the track, visible only when there's actually more to scroll to — see
 *  `Carousel.tsx`'s own doc comment) instead of wrapping tabs onto a second row, composing the
 *  already-ported `Carousel.tsx` (also what `TokenCarousel` in Mapping/Rename is built on) rather
 *  than duplicating Cantemo's own separate inline scroll/fade/arrow implementation. `flex: 1` +
 *  `min-width: 0` let it share `TabsRow` with `HeaderRightGroup` without pushing that group off
 *  the row — the carousel's own internal `overflow-x: auto` absorbs any extra tab width instead
 *  of the flex row doing it.
 *
 *  This replaces an earlier hybrid (a `Tabs`-based 2-row-wrap above 700px, this carousel only
 *  below it) that was built specifically to preserve the tablet 2-row-wrap UX — Addison's
 *  explicit follow-up ask was to drop that split and make the carousel the only tab bar at every
 *  width, tablet/desktop included. */
const TabsCarousel = styled(Carousel)`
  flex: 1;
  min-width: 0;
`;

/** Below the mobile breakpoint, `HeaderRightGroup`'s fixed-width contents (the progress meter's
 *  120px bar, see `FieldsScreen`'s `HeaderProgressBar`) don't leave the tab carousel enough
 *  width to stay usable alongside it, so mobile stacks the two into their own full-width rows
 *  instead of sharing one. Also the shared ancestor for `--gray300`/`--blue300` — see
 *  `TabsCarousel`'s doc comment above. */
const TabsRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 12px;
  flex-wrap: nowrap;
  --gray300: var(--spotterHeaderMuted);
  --blue300: var(--spotterAccentBright);

  @media (max-width: ${MOBILE_MAX_WIDTH}) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

const HeaderRightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-left: auto;
  /* Unconditional (not just below the mobile breakpoint): without it the default flex-shrink: 1
     lets this group's fixed-width contents (e.g. the 120px progress bar in FieldsScreen's
     headerExtra) get squeezed narrower than their own box at any width TabsRow is tight on
     room. */
  flex-shrink: 0;

  @media (max-width: ${MOBILE_MAX_WIDTH}) {
    margin-left: 0;
    /* A page passing BOTH \`headerExtra\` and \`changes\` (only \`FieldsScreen\` does today) has two
     * fixed-width, non-shrinking children here — side by side they're wider than a phone
     * viewport, pushing the whole page into horizontal scroll. Wrapping keeps each on its own
     * line instead. */
    flex-wrap: wrap;
  }
`;

/** See "Tab identity" in the doc comment above for why this lives here and not in
 *  `public/index.html`. Round 18: takes `ready` (`useInterfaceAccess`'s "granted" check) — the
 *  BROWSER tab strip is as much "what the tool's about" as anything rendered in-page, so it stays
 *  on whatever title/favicon the document already had until access is actually granted, same as
 *  every other identifying bit of this header. */
const useSpotterTabIdentity = (ready: boolean) => {
  useEffect(() => {
    if (!ready) {
      return undefined;
    }
    const previousTitle = document.title;
    document.title = SPOTTER_WORDMARK;

    return () => {
      document.title = previousTitle;
    };
  }, [ready]);
};

/** Publishes this header's REAL rendered height as `--spotterHeaderHeight` on the document root, so
 *  `FieldsScreen`/`FrScreen`/`RenameScreen`'s sticky `Aside`/`PageWrapperTwoColumn` `stickyTop` can sit
 *  flush under the header (`var(--spotterHeaderHeight, 118px)`) instead of a hand-tuned pixel guess —
 *  the header's actual height drifts as its own content changes (org-name breadcrumb loading in,
 *  a font swap, tab label wrapping on a narrow window), and a stale hardcoded number either left
 *  a gap or, worse, let the sticky pane render partially UNDER the header (same `z-index` stack
 *  but visually starting too high). `ResizeObserver` keeps it correct across those changes, not
 *  just at mount. Global (`documentElement`), since `Aside` is a DOM sibling of `Header`, not a
 *  descendant — a CSS var only reaches it via a shared ancestor. Reset on unmount for the same
 *  route-scoping reason `useSpotterTabIdentity` restores the title. */
const useHeaderHeightVar = (ref: React.RefObject<HTMLElement>) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const setVar = () => {
      document.documentElement.style.setProperty(
        "--spotterHeaderHeight",
        `${el.getBoundingClientRect().height}px`
      );
    };
    setVar();
    const observer = new ResizeObserver(setVar);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--spotterHeaderHeight");
    };
  }, [ref]);
};

/** Each `spotter/*` tab is its own route (`AppRouter.tsx`), and every one of those screens
 *  renders its own `SpotterHeader` — so `SpotterTabs` fully unmounts and remounts on every tab
 *  click, losing the carousel's scroll position with it. Left alone, a fresh mount always starts
 *  at `scrollLeft: 0`, so the scroll-into-view effect below would visibly slide the WHOLE way
 *  over from the left edge on every navigation, even if the user had already been scrolled over
 *  toward the target tab. A plain module-scoped variable (not component state, which resets on
 *  remount same as the DOM does) survives that remount so the next mount can restore it. */
let lastTabsScrollLeft = 0;

/** `TabsCarousel`'s tab row — separate component (not inlined in `SpotterHeader`) purely so the
 *  active-tab-scroll-into-view effect below has its own clean dependency array keyed on the
 *  route, without dragging `SpotterHeader`'s own unrelated state into that effect's deps.
 *  Auto-scrolls the active tab into view (mirrors Cantemo's own `Tabs`'s `scrollTabIntoView`) via
 *  the DOM's native `scrollIntoView` on the active tab's own element rather than reimplementing
 *  Cantemo's manual scrollLeft/margin math — the tab sits inside `Carousel`'s own
 *  `overflow-x: auto` container, which is exactly what `scrollIntoView({ inline: "nearest" })`
 *  already knows how to scroll. `block: "nearest"` keeps this from ever scrolling the PAGE
 *  vertically — only relevant if the header were off-screen when a tab is activated, which can't
 *  happen here since tapping a tab requires the header (and thus the tab itself) to already be
 *  visible. `scroll-margin-inline` on `TabButton` keeps the scrolled-to tab clear of the arrow/
 *  fade overlay (see its own doc comment) instead of landing flush under it. */
const SpotterTabs = ({ needsAttention }: { needsAttention: boolean }) => {
  const location = useLocation();
  const tabRefs = useRef<Record<string, HTMLElement | null>>({});
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeKey = SPOTTER_TOP_TABS.find((t) =>
    location.pathname.startsWith(t.key)
  )?.key;

  // Runs before paint, so the restore never flashes at scrollLeft 0 first — see
  // `lastTabsScrollLeft`'s doc comment above for why this is needed at all.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollLeft = lastTabsScrollLeft;
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const trackScrollLeft = () => {
      lastTabsScrollLeft = el.scrollLeft;
    };
    el.addEventListener("scroll", trackScrollLeft);
    return () => el.removeEventListener("scroll", trackScrollLeft);
  }, []);

  useEffect(() => {
    if (!activeKey) {
      return;
    }
    tabRefs.current[activeKey]?.scrollIntoView({
      behavior: "smooth",
      inline: "nearest",
      block: "nearest",
    });
  }, [activeKey]);

  return (
    <TabsCarousel
      fadeColor="var(--spotterSlate)"
      arrowSize={28}
      arrowIconSize={16}
      justify="start"
      scrollRef={scrollRef}
    >
      {SPOTTER_TOP_TABS.map((tab) => (
        <TabButton
          key={tab.key}
          as={NavLink}
          to={tab.key}
          ref={(element: HTMLElement | null) => {
            tabRefs.current[tab.key] = element;
          }}
        >
          {tab.label}
          {tab.key === CONTROL_ROOM_TAB_KEY &&
            needsAttention &&
            activeKey !== CONTROL_ROOM_TAB_KEY && (
              <NeedsAttentionDot
                role="img"
                aria-label={SPOTTER_STRINGS.controlRoom.needsAttentionTabDot}
              />
            )}
        </TabButton>
      ))}
    </TabsCarousel>
  );
};

interface SpotterHeaderProps {
  changes?: SpotterChangesPanelProps;
  /** Optional extra content rendered in the tab row, grouped with and left of the changes trigger. */
  headerExtra?: React.ReactNode;
}

/** Only renders header CHROME — the "denied" content notice is each mount site's own job (every
 *  screen already gates its content area on `useInterfaceAccess` before rendering this, see
 *  `FieldsScreen.tsx` etc.; `SpotterTabLoadingShell` does too, for the window before a screen's
 *  own chunk has even loaded). Rendering a notice HERE too would double it up under every screen
 *  that already shows its own. */
const SpotterHeader = ({ changes, headerExtra }: SpotterHeaderProps) => {
  const access = useInterfaceAccess(SPOTTER_INTERFACE_ID);
  const ready = access.status === "granted";
  const orgName = access.status === "granted" ? access.customerName : undefined;
  const headerRef = useRef<HTMLElement>(null);
  useSpotterTabIdentity(ready);
  useHeaderHeightVar(headerRef);
  const needsAttention = useControlRoomNeedsAttention(ready);

  return (
    <Header ref={headerRef}>
      <Top>
        <Lockup>
          {ready ? (
            <>
              <SpotterMark />
              <Brand>{SPOTTER_WORDMARK}</Brand>
            </>
          ) : (
            <>
              <MarkSkeleton aria-hidden="true" />
              <BrandSkeleton aria-hidden="true" />
            </>
          )}
          <StagingBadge />
        </Lockup>
        <Crumb>
          {SPOTTER_STRINGS.header.breadcrumbProduct}{" "}
          {ready ? (
            <>
              &nbsp;·&nbsp; <span>{orgName}</span>
            </>
          ) : (
            <>
              &nbsp;·&nbsp; <CrumbSkeleton aria-hidden="true" />
            </>
          )}
        </Crumb>
        <Spacer />
        <PoweredBy href={CINESYS_URL} target="_blank" rel="noopener noreferrer">
          <PoweredByLabel>Powered by</PoweredByLabel>
          <CinesysLogo height={34} />
        </PoweredBy>
      </Top>
      <TabsRow>
        {ready ? (
          <SpotterTabs needsAttention={needsAttention} />
        ) : (
          <SpotterTabsSkeleton />
        )}
        {ready && (headerExtra || changes) && (
          <HeaderRightGroup>
            {headerExtra}
            {changes && <SpotterHeaderChanges {...changes} />}
          </HeaderRightGroup>
        )}
      </TabsRow>
    </Header>
  );
};

export default SpotterHeader;
